/* eslint-disable camelcase */
/* eslint-disable import/no-extraneous-dependencies */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'

import { Context, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import axios from 'axios'
import Cookies from 'js-cookie'
import { array, bool, object, shape, string } from 'prop-types'
import ReactDOM from 'react-dom'
import { throttle } from 'throttle-debounce'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import i18n from 'Pods/Shared/I18n'
import ModelSearchTags from 'Pods/Shared/ModelSearch/ModelSearchTags'
import { localizedRoute } from 'Pods/Shared/paths'
import getLocaleParam from 'Pods/Shared/Utils/getLocaleParam'

import CheckIcon from 'images/icons/svg/listing_page/check.svg'

import getFileTypeSlug from 'Utils/getFileTypeSlug'
import QueryToJson from 'Utils/QueryToJson'
import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'
import { safeTrackSuggestionClick, safeTrackingUserSettings } from 'Utils/TrackingUtils'
import useBreakpoint from 'Utils/useBreakpoint'

import DownloadModal from './Components/DownloadModal/DownloadModal'
import RemainingFilters from './Components/Filters/Components/RemainingFilters/LazyRemainingFilters'
import Filters from './Components/Filters/Filters'
import FollowingNotice from './Components/FollowingNotice'
import FooterBanners from './Components/FooterBanners/FooterBanners'
import FreeItemsSection from './Components/FreeItemsSection/FreeItemsSection'
import List from './Components/List'
import Pagination from './Components/Pagination/Pagination'
import SuggestedSearchNotice from './Components/SuggestedSearchNotice'
import UpsellBlock from './Components/UpsellBlock/UpsellBlock'
import getListingBasePath from './Components/utils/getListingBasePath'
import useCategoryNavigation from './Hooks/useCategoryNavigation'
import { maxPolygonThreshold, maxPriceThreshold, minPolygonThreshold, minPriceThreshold } from './Static/Constants'
import { sortFileTypesBySelection } from './Utils/sortFileTypesBySelection'

const queryClient = new QueryClient()


function applyLocalePrefix(url = '') {
  const locale = getLocaleParam()

  if (!locale) {
    return url
  }

  const normalizedPath = url.startsWith('/') ? url : `/${url}`

  const startsWithLocaleSlash = normalizedPath.startsWith(`/${locale}/`)
  const equalsLocale = normalizedPath === `/${locale}`
  const startsWithLocaleQuery = normalizedPath.startsWith(`/${locale}?`)

  if (startsWithLocaleSlash || equalsLocale || startsWithLocaleQuery) {
    return normalizedPath
  }

  return `/${locale}${normalizedPath}`
}

function localizeBackendUrl(url, locale) {
  if (!locale || locale === 'en' || !url) return url

  const localModels = localizedRoute({ key: 'models', locale })
  const localPrintModels = localizedRoute({ key: 'print-models', locale })

  // Backend always returns English paths, replace with localized
  return url
    .replace(/\/3d-print-models/g, localPrintModels)
    .replace(/\/3d-models/g, localModels)
}

function ListingPage({
  sortByValue,
  sortByOptions,
  filtersOptions,
  extractingCoreStyles = false,
  data = EMPTY_ARRAY,
  meta,
  browserAttributes: initialBrowserAttributes,
  itemListId: initialItemListId,
  tags: initialTags = EMPTY_ARRAY,
  params,
  seoTags = EMPTY_ARRAY,
  isMobileSSR = false,
  isSimilarSearchPossible = false,
  saveSearchSettings = null,
  tagLinks = EMPTY_OBJECT,
  isSalesBoostEnabled = false,
  subscriptionBadgesEnabled = false,
  hideSuggestionsSection = false,
  isUserLoggedIn = false,
  licenseFilter = null,
  suggestedSearch = null,
  shouldShowAllItemsOnMobile = false,
  freeItems: initialFreeItems = EMPTY_OBJECT,
}) {
  const [currentSuggestedSearch, setCurrentSuggestedSearch] = useState(suggestedSearch)
  const [trackingInfo, setTrackingInfo] = useState({
    isInfoAvailable: false,
    areUtilsAvailable: typeof window !== 'undefined' && typeof window.handleImpression === 'function',
  })
  const currentBreakpoint = useBreakpoint()
  const [hasHydrated, setHasHydrated] = useState(false)
  // Keep the first client render identical to the server: the cached fast-page HTML only knows
  // isMobileSSR (useBreakpoint is undefined at SSR), so folding in the live breakpoint before
  // hydration flips isMobile on mobile and mismatches the server markup, tearing down the tree
  // (Filters/List/ModelCard/Pagination/FooterBanners) via a synchronous flushSync client render.
  const isMobile = hasHydrated ? (isMobileSSR || ['xs'].includes(currentBreakpoint)) : isMobileSSR

  useEffect(() => {
    setHasHydrated(true)
  }, [])
  const pageConfig = usePageConfig()
  const subscriptionFlowEnabled = !!pageConfig?.subscriptionFlowEnabled
  const premiumListingLabelEnabled = !!pageConfig?.premiumExposed
  const subscriptionV17CatalogEnabled = !!pageConfig?.subscriptionV17CatalogEnabled
  const businessAccountSearchDownload = !!pageConfig?.businessAccountSearchDownload
  const [isCurrentUser, setIsCurrentUser] = useState(false)
  const [currentUserId, setCurrentUserId] = useState(undefined)
  const [viewerIsAdult, setViewerIsAdult] = useState(false)
  const [showCgtVerification, setShowCgtVerification] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [shouldUpdateTags, setShouldUpdateTags] = useState(false)
  const [showFiltersBar, setShowFiltersBar] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState({})
  const [lastUsedFilters, setLastUsedFilters] = useState(null)
  const [currentModels, setCurrentModels] = useState(data)
  const [downloadAccess, setDownloadAccess] = useState(EMPTY_OBJECT)
  const [downloadModalHref, setDownloadModalHref] = useState(null)
  const [currentFiltersOptions, setCurrentFiltersOptions] = useState(filtersOptions)
  const [currentMeta, setMeta] = useState(meta)
  const [currentFreeItems, setCurrentFreeItems] = useState(initialFreeItems)
  const [cart, setCart] = useState({})
  const [rawTags, setRawTags] = useState(initialTags)
  const [tags, setTags] = useState(formatTags(initialTags))
  const [cardSizeSetting, setCardSizeSetting] = useState('medium')
  const [shouldTrackSearch, setShouldTrackSearch] = useState(true)
  const [searchEnchantmentAction, setSearchEnchantmentAction] = useState({})
  const [gaList, setGaList] = useState('')
  const [itemListId, setItemListId] = useState(initialItemListId)
  const [canLoadMobileRemainingFilters, setCanLoadMobileRemainingFilters] = useState(false)
  const [shouldResetPriceRange, setShouldResetPriceRange] = useState(false)
  const [shouldResetPolygonRange, setShouldResetPolygonRange] = useState(false)
  const [browserAttributes, setBrowserAttributes] = useState(initialBrowserAttributes)
  const [isSSRModelsData, setIsSSRModelsData] = useState(true)
  const [cardVisibilitySettings, setCardVisibilitySettings] = useState({
    title: true,
    type: true,
    price: true,
    format: true,
    quickActionsOnHover: true,
    cgtStandard: !!showCgtVerification,
    suggestions: true,
  })
  const [customPagesChecks, setCustomPagesChecks] = useState({
    isFree3dModelsPage: undefined,
    isFree3dPrintModelsPage: undefined,
    isPbr3dModelsPage: undefined,
    isLowPoly3dModelsPage: undefined,
    is3dPrintModelsPage: undefined,
    isRigged3dModelsPage: undefined,
    isAnimated3dModelsPage: undefined,
    isSearchPage: undefined,
  })
  const [followingCount, setfollowingCount] = useState(undefined)
  const [showNotice, setShowNotice] = useState(false)

  const saveFiltersToCookies = useCallback((filters) => {
    try {
      Cookies.set('selectedFilters', JSON.stringify(filters), {
        expires: 30,
        path: '/',
      })
    } catch (error) {
      console.error('Error saving filters to cookies:', error)
    }
  }, [])

  const saveParamsToCookies = useCallback((obj) => {
    try {
      if (obj.params === undefined || Object.keys(obj).length === 0) {
        return Cookies.remove('paramsToRedirect', { path: '/' })
      }
      Cookies.set('paramsToRedirect', JSON.stringify(obj.params), {
        expires: 30,
        path: '/',
      })
    } catch (error) {
      console.error('Error saving params to cookies:', error)
    }
  }, [])

  useEffect(() => {
    setCardVisibilitySettings(getCardVisibilitySettings())
    setCardSizeSetting(getCardSizeSetting())
    setCanLoadMobileRemainingFilters(true)
    fetchTrackingInfo(currentModels)

    window.addEventListener('navigation-cart-loaded', setCartInfo)
    window.addEventListener('window-config-loaded', readPageConfig)

    // If react initialized too late
    if (window?.pageConfig) {
      window.dispatchEvent(new CustomEvent('window-config-loaded'))
      window.dispatchEvent(
        new CustomEvent('navigation-cart-loaded', {
          detail: window.pageConfig.cart,
        })
      )
    }

    const fusetag = window.fusetag

    if (fusetag && browserAttributes.searchingFree) {
      fusetag.registerZone('first-body-ad')
      fusetag.pageInit()
    }

    return () => {
      window.removeEventListener('navigation-cart-loaded', setCartInfo)
      window.removeEventListener('window-config-loaded', readPageConfig)
    }
  }, [])

  const currentModelIdsKey = currentModels.map((model) => model.id).join(',')

  // Direct download from search (business accounts): asks the backend which of
  // the visible models the user may download, and where each download file
  // list lives. Gated server-side too - the response is empty for everyone else.
  const fetchDownloadAccess = useCallback(async (models = []) => {
    const ids = models.map((model) => model.id)

    if (!ids.length) {
      return
    }

    const pathSegments = [params.parentCategorySlug, params.subcategorySlug].filter(Boolean)
    let link = ['/listing_page/download_access', ...pathSegments].filter(Boolean).join('/')

    if (!pathSegments.length) {
      const [, ...splitPath] = window.location.pathname.split('/').filter(Boolean)
      link = ['/listing_page/download_access', ...splitPath].filter(Boolean).join('/')
    }

    const localeParam = getLocaleParam()
    const query = [`item_ids=${ids}`, localeParam ? `locale=${localeParam}` : null].filter(Boolean).join('&')

    try {
      const response = await window.fetchJSON(`${link}?${query}`)
      const access = response?.access || {}

      const normalized = Object.fromEntries(
        Object.entries(access).map(([itemId, value]) => [itemId, { kind: value.kind, filesHref: value.files_href }])
      )

      setDownloadAccess((prev) => ({ ...prev, ...normalized }))
    } catch (error) {
      // Non-blocking: cards simply render without a download button on failure.
    }
  }, [params.parentCategorySlug, params.subcategorySlug])

  useEffect(() => {
    if (businessAccountSearchDownload && currentModelIdsKey) {
      fetchDownloadAccess(currentModels)
    }
  }, [businessAccountSearchDownload, currentModelIdsKey, fetchDownloadAccess])

  const pageTopRef = useRef(null)
  const scrollObserverRef = useRef(null)
  const scrollTimeoutRef = useRef(null)

  function setCartInfo(event) {
    window.removeEventListener('navigation-cart-loaded', setCartInfo)
    setCart(event.detail)
  }

  function readPageConfig() {
    window.removeEventListener('window-config-loaded', readPageConfig)
    setIsCurrentUser(window?.pageConfig?.isCurrentUser || false)
    setCurrentUserId(window?.pageConfig?.currentUserId || undefined)
    setViewerIsAdult(window?.pageConfig?.viewerIsAdult || false)
    setShowCgtVerification(window?.pageConfig?.showCgtVerification || false)
    setfollowingCount(window?.pageConfig?.followingCount)
  }

  function getInitialFilters(queryParams) {
    if (!queryParams) {
      return
    }

    const currentPathname = window.location.pathname.split('/').filter(Boolean)
    const location = currentPathname[0]

    const pageChecks = {
      isFree3dModelsPage: getInitialPageType(location === 'free-3d-models'),
      isFree3dPrintModelsPage: getInitialPageType(location === 'free-3d-print-models'),
      isPbr3dModelsPage: getInitialPageType(location === 'pbr-3d-models'),
      isLowPoly3dModelsPage: getInitialPageType(location === 'low-poly-3d-models'),
      is3dPrintModelsPage: getInitialPageType(location === '3d-print-models'),
      isRigged3dModelsPage: getInitialPageType(location === 'rigged-3d-models'),
      isAnimated3dModelsPage: getInitialPageType(location === 'animated-3d-models'),
      isSearchPage: getInitialPageType(location === 'search' && !queryParams.keywords),
    }

    setCustomPagesChecks(pageChecks)

    const {
      isFree3dModelsPage,
      isFree3dPrintModelsPage,
      isPbr3dModelsPage,
      isLowPoly3dModelsPage,
      is3dPrintModelsPage,
      isRigged3dModelsPage,
      isAnimated3dModelsPage,
    } = pageChecks

    const {
      premium,
      buyer_engagement_program: buyerEngagementProgram,
      sale_off: saleOff,
      free,
      collection,
      subscribed,
      keywords,
      suggested_keywords,
      exclude_free: excludeFree,
      price_range: priceRange,
      polygon_range: polygonRange,
      image_id: imageId,
      ...otherFilters
    } = queryParams

    const excludedFilterKeys = [
      'print_ready',
      'low_poly',
      'animated',
      'rigged',
      'pbr',
      'file_types',
      'with_makes',
      'pre_supported',
      'multiple_parts',
      'cgt_verified',
    ]

    const parsedOtherFilters = Object.fromEntries(
      Object.entries(otherFilters).filter(
        ([key]) => !excludedFilterKeys.includes(key)
      )
    )
    const {
      printReady,
      lowPoly,
      animated,
      rigged,
      pbr,
      fileTypes,
      following,
      withMakes,
      preSupported,
      multipleParts,
    } = params

    const arePricingFiltersPresent =
      premium ||
      buyerEngagementProgram ||
      saleOff ||
      free ||
      collection ||
      subscribed ||
      excludeFree ||
      isFree3dModelsPage ||
      isFree3dPrintModelsPage

    const areModelTypeFiltersPresent =
      is3dPrintModelsPage ||
      isFree3dPrintModelsPage ||
      printReady ||
      isAnimated3dModelsPage ||
      animated ||
      isPbr3dModelsPage ||
      pbr ||
      isRigged3dModelsPage ||
      rigged ||
      isLowPoly3dModelsPage ||
      lowPoly ||
      withMakes ||
      preSupported ||
      multipleParts

    const areDesignersFiltersPresent = following

    const initialLicenses = licenseFilter || queryParams.licenses
    const areLicensesFiltersPresent = initialLicenses && initialLicenses.length > 0

    const filtersObject = {
      ...parsedOtherFilters,
      fileTypes,
      priceRange,
      polygonRange,
      imageId,
      keywords: keywords || '',
      suggested_keywords: suggested_keywords || '',
      ...(arePricingFiltersPresent && {
        pricing: {
          premium,
          sale_off: saleOff,
          free: isFree3dModelsPage || isFree3dPrintModelsPage || free,
          collection,
          subscribed,
          exclude_free: excludeFree,
        },
      }),
      ...(areModelTypeFiltersPresent && {
        modelType: {
          print_ready: is3dPrintModelsPage || isFree3dPrintModelsPage || printReady,
          animated: isAnimated3dModelsPage || animated,
          pbr: isPbr3dModelsPage || pbr,
          rigged: isRigged3dModelsPage || rigged,
          low_poly: isLowPoly3dModelsPage || lowPoly,
          with_makes: withMakes,
          pre_supported: preSupported,
          multiple_parts: multipleParts,
        },
      }),
      ...(areDesignersFiltersPresent && {
        designers: {
          following,
        },
      }),
      ...(areLicensesFiltersPresent && {
        licenses: Array.isArray(initialLicenses) ? initialLicenses : [initialLicenses],
      }),
      sort_by: queryParams.sort_by,
    }

    setSelectedFilters(filtersObject)
  }

  function resetFilters() {
    const { keywords } = selectedFilters
    const { totalCount } = currentMeta
    const { parentCategorySlug: category } = params

    window.resetTracker({
      searchTerm: keywords || category || '',
      currentModelsCount: totalCount,
    })

    setIsLoading(true)
    setSelectedFilters((state) => {
      const newFiltersObject = {
        keywords: state.keywords,
        suggested_keywords: state.suggested_keywords,
        page: getPageParam(1, params.parentCategorySlug),
      }

      if (saveSearchSettings) {
        saveFiltersToCookies({})
        saveParamsToCookies({})
      }

      const payload = getQueryParams(newFiltersObject, true)

      getData({
        payload,
        callback: () => {
          setIsLoading(false)
          setSearchEnchantmentAction({ type: 'filter', value: 'reset' })
          triggerTracking()
        },
      })

      setShouldResetPriceRange(true)
      setShouldResetPolygonRange(true)
      setCustomPagesChecks({
        isFree3dModelsPage: undefined,
        isFree3dPrintModelsPage: undefined,
        isPbr3dModelsPage: undefined,
        isLowPoly3dModelsPage: undefined,
        is3dPrintModelsPage: undefined,
        isRigged3dModelsPage: undefined,
        isAnimated3dModelsPage: undefined,
        isSearchPage: undefined,
      })

      return newFiltersObject
    })
    window.removeCurrentSearchFilter()
  }

  const getData = useCallback(
    ({ payload, callback = null, shouldUpdateURL = true, customListingPath = null }) => {
      setShouldUpdateTags(true)
      const locale = getLocaleParam()
      const localeFromI18n = typeof window !== 'undefined' ? window.I18n?.locale : null
      const localeFromHtml = typeof document !== 'undefined'
        ? document.documentElement?.getAttribute('lang')
        : null
      const rawLocale = localeFromI18n || localeFromHtml || null
      let localeSource = null
      if (localeFromI18n) {
        localeSource = 'window.I18n.locale'
      } else if (localeFromHtml) {
        localeSource = 'html.lang'
      }
      const localePrefix = locale ? `/${locale}` : ''
      const listingPath = customListingPath || getListingBasePath()
      const url = `${localePrefix}/${listingPath}.json?current_path=${listingPath}`
      const queryFunction = () => axios.get(url, payload)

      const onSuccess = ({
        data: {
          data: newData,
          meta: newMeta,
          filtersOptions: newFiltersOptions,
          gaList: newGaList,
          itemListId: newItemListId,
          browser_attributes: newBrowserAttributes,
          tags: newTags,
          url: newUrl,
          suggested_search: newSuggestedSearch,
          freeItems: newFreeItems,
        },
      }) => {
        setCurrentModels(newData)
        setMeta(newMeta)
        setGaList(newGaList)
        setItemListId(newItemListId)
        setCurrentFiltersOptions(newFiltersOptions)
        setBrowserAttributes(newBrowserAttributes)
        setIsSSRModelsData(false)
        setRawTags(newTags)
        setCurrentSuggestedSearch(newSuggestedSearch || null)
        setCurrentFreeItems(newFreeItems || EMPTY_OBJECT)

        if (shouldUpdateURL) {
          const localizedUrl = localizeBackendUrl(newUrl, locale)
          window.history.pushState({}, document.title, applyLocalePrefix(localizedUrl))
        }

        if (callback) {
          callback(newData)
        }
      }

      const onError = (error) => {
        setIsLoading(false)

        if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
          return
        }

        const status = error?.response?.status
        const EXPECTED_HTTP_STATUSES = [404, 410]

        if (EXPECTED_HTTP_STATUSES.includes(status)) {
          return
        }

        window.showNotification('An error occurred. Please try again.', {
          type: 'error',
          autohide: true,
        })

        if (window.Sentry) {
          window.Sentry.captureException(error, {
            tags: {
              component: 'ListingPage',
              action: 'getData',
            },
            extra: {
              url,
              payload: payload?.params,
              listingPath,
              rawLocale,
              normalizedLocale: locale,
              localeSource,
              status,
              requestUrl: error?.config?.url,
              requestParams: error?.config?.params,
              responseUrl: error?.request?.responseURL,
            },
          })
        }
      }

      const getCacheKeyParams = () => {
        const { page, ...rest } = payload?.params || {}
        const filteredRest = Object.fromEntries(
          Object.entries(rest)
            .map(([key, value]) => [key, Array.isArray(value) && value.length === 0 ? undefined : value])
            .filter((entry) => entry[1] !== undefined)
        )

        return { ...filteredRest, page: page || 1 }
      }
      const cacheKeyParams = getCacheKeyParams()

      queryClient
        .fetchQuery({
          queryKey: ['listingPage', url, cacheKeyParams],
          queryFn: queryFunction,
          staleTime: 10 * 60 * 1000, // 10 minutes
        })
        .then(onSuccess)
        .catch(onError)
    },
    []
  )

  const throttledGetData = useMemo(() => throttle(1000, getData), [getData])

  function triggerTracking() {
    setShouldTrackSearch(true)
  }

  function findMissingArrayElement(arr1, arr2) {
    const missingElements = arr1.filter((element) => !arr2.includes(element))
    return missingElements.length > 0 ? missingElements[0] : null
  }

  function getLastSelectedFilter(currentFilters, name, value) {
    if (!value) {
      return
    }

    if (['modelType', 'pricing'].includes(name)) {
      const keys = Object.keys(value)
      const filterKeys = Object.keys(currentFilters?.[name] || {}).reduce((acc, key) => {
        if (currentFilters?.[name]?.[key] !== undefined) {
          return [...acc, key]
        }
        return acc
      }, [])
      if (keys.length > filterKeys.length) {
        setSearchEnchantmentAction({
          type: 'filter',
          value: keys[keys.length - 1],
        })
      } else {
        setSearchEnchantmentAction({
          type: 'remove_filter',
          value: findMissingArrayElement(filterKeys, keys),
        })
      }
    } else if (['fileTypes', 'licenses', 'compatible', 'priceRange', 'polygonRange', 'miscellaneous'].includes(name)) {
      const addFilterTypes = {
        fileTypes: 'filter',
        licenses: 'filter',
        compatible: 'compatible',
        priceRange: 'price_range',
        polygonRange: 'polygon_range',
        miscellaneous: 'miscellaneous',
      }

      const valueLength = value.length
      const currentFiltersItems = currentFilters?.[name] || []

      if (valueLength > 0) {
        const currentFiltersLength = currentFiltersItems.length
        if (valueLength > currentFiltersLength) {
          const addedValue =
            name === 'fileTypes' ? getFileTypeSlug(value[value.length - 1], filtersOptions) : value[value.length - 1]
          setSearchEnchantmentAction({
            type: addFilterTypes[name],
            value: addedValue,
          })
        } else {
          const removedValue = findMissingArrayElement(currentFiltersItems, value)
          const removedFilter =
            name === 'fileTypes'
              ? getFileTypeSlug(removedValue, filtersOptions)
              : findMissingArrayElement(currentFiltersItems, value)
          setSearchEnchantmentAction({
            type: 'remove_filter',
            value: removedFilter,
          })
        }
      } else {
        const removedFilter =
          name === 'fileTypes' ? getFileTypeSlug(currentFiltersItems[0], filtersOptions) : currentFiltersItems[0]
        setSearchEnchantmentAction({
          type: 'remove_filter',
          value: removedFilter,
        })
      }
    }
  }

  const getPageParam = (page, isCategoryPage) => {
    if (isCategoryPage && Number(page) === 1) {
      return undefined
    }

    return page
  }

  const handleFilterChange = useCallback(
    ({
      name = '',
      value = '',
      callback = () => {},
      skipFetch = false,
      skipGetLastSelectedFilter = false,
      newObject = null,
      clearLastUsedFilters = true,
      customListingPath = null,
    }) => {
      setIsLoading(true)
      setSelectedFilters((state) => {
        if (!skipGetLastSelectedFilter) {
          getLastSelectedFilter(state, name, value)
        }
        if (clearLastUsedFilters) {
          setLastUsedFilters(null)
        }
        const newFiltersObject = newObject || { ...state, [name]: value }
        const isPageUpdated = name === 'page'

        const { pricing, page, modelType, fileTypes, priceRange, polygonRange, designers, imageId, ...otherFilters } =
          newFiltersObject

        const newFilters = {
          ...(isPageUpdated ? { page: getPageParam(page, params.parentCategorySlug) } : { page: undefined }),
          ...(imageId ? { image_id: imageId } : {}),
          ...pricing,
          ...modelType,
          ...designers,
          ...otherFilters,
          ...(name === 'priceRange' ? { price_range: value } : {}),
          ...(name !== 'priceRange' && priceRange ? { price_range: priceRange } : {}),
          ...(name === 'polygonRange' ? { polygon_range: value } : {}),
          ...(name !== 'polygonRange' && polygonRange ? { polygon_range: polygonRange } : {}),
          ...(name === 'fileTypes' ? { file_types: value } : {}),
          ...(name !== 'fileTypes' && fileTypes ? { file_types: fileTypes } : {}),
        }
        const payload = getQueryParams(newFilters)
        const requestCallback = isPageUpdated
          ? (newModels) => {
              setIsLoading(false)
              scrollToTop()
              callback()
              triggerTracking()
              fetchTrackingInfo(newModels)
            }
          : (newModels) => {
              setIsLoading(false)
              callback()
              triggerTracking()
              fetchTrackingInfo(newModels)
            }

        if (!skipFetch) {
          throttledGetData({
            payload,
            callback: requestCallback,
            customListingPath,
          })
        }

        if (saveSearchSettings) {
          saveFiltersToCookies(newFiltersObject)
          saveParamsToCookies(payload)
        }

        return newFiltersObject
      })
    },
    [selectedFilters]
  )

  useCategoryNavigation({
    isLoading,
    selectedFilters,
    handleFilterChange,
  })

  function getQueryParams(newFilters) {
    const filtersKeys = Object.keys(newFilters)

    const parsedFilters = filtersKeys.reduce((acc, key) => {
      const currentValue = newFilters[key]

      if (key !== 'metaverses' && currentValue) {
        acc[key] = currentValue
      }

      return acc
    }, {})

    if (params.parentCategorySlug && !parsedFilters.parent_category_slug) {
      parsedFilters.parent_category_slug = params.parentCategorySlug
    }

    if (params.subcategorySlug && !parsedFilters.subcategory_slug) {
      parsedFilters.subcategory_slug = params.subcategorySlug
    }

    return { params: { ...parsedFilters } }
  }

  const handleDifferentTypesOfKeywords = useCallback((keywords) => {
    if (Array.isArray(keywords)) {
      return keywords.map((keyword) => keyword)
    }

    return keywords.split(',')
  }, [])

  const processKeywordsValue = useCallback(({ action, value, keywords }) => {
    const currentKeywordsValue = keywords ? handleDifferentTypesOfKeywords(keywords) : []

    if (action === 'add') {
      return [...currentKeywordsValue, value].join(',')
    }

    if (action === 'remove') {
      return currentKeywordsValue.filter((keyword) => keyword !== value).join(',')
    }

    if (action === 'replace') {
      return value
    }

    return ''
  }, [])

  const onSuggestedKeywordsClick = ({ suggestedKeyword }) => {
    const currentKeywords = selectedFilters.suggested_keywords
    const parsedKeyword = suggestedKeyword.replaceAll(' ', '-')
    if (!currentKeywords) {
      return handleFilterChange({
        name: 'suggested_keywords',
        value: processKeywordsValue({
          action: 'add',
          value: parsedKeyword,
          keywords: currentKeywords,
        }),
      })
    }

    if (currentKeywords && currentKeywords.length && !currentKeywords.split(',').includes(parsedKeyword)) {
      return handleFilterChange({
        name: 'suggested_keywords',
        value: processKeywordsValue({
          action: 'add',
          value: parsedKeyword,
          keywords: currentKeywords,
        }),
      })
    }

    return null
  }

  const onDeleteTagClick = useCallback(
    ({ value: valueToDelete }) => {
      const qualityVerifiedLabel = i18n.t('browser.filters.cgt_standard_only', {
        ns: 'rails',
        lng: params?.locale || 'en',
      })

      if (valueToDelete === qualityVerifiedLabel) {
        const newLicenses = (selectedFilters.licenses || []).filter((license) => license !== 'cgt_standard_only')
        handleFilterChange({ name: 'licenses', value: newLicenses })
        window.removeCurrentSearchFilter({ name: 'licenses', value: 'cgt_standard_only' })
      } else {
        const currentKeywords = selectedFilters.suggested_keywords
        const newKeywordsQuery = processKeywordsValue({
          action: 'remove',
          value: valueToDelete.replaceAll(' ', '-'),
          keywords: currentKeywords,
        })

        handleFilterChange({
          name: 'suggested_keywords',
          value: newKeywordsQuery,
        })
      }

      setSearchEnchantmentAction({ type: 'remove_tag', value: valueToDelete })
    },
    [
      selectedFilters.suggested_keywords,
      selectedFilters.licenses,
      handleFilterChange,
      params?.locale,
      processKeywordsValue,
      setSearchEnchantmentAction,
    ]
  )

  const showFilters = useCallback(() => setShowFiltersBar(true), [])
  const hideFilters = useCallback(() => setShowFiltersBar(false), [])

  const scrollToTop = useCallback(() => {
    if (pageTopRef && pageTopRef.current) {
      // Clean up any existing observer and timeout from previous scroll
      if (scrollObserverRef.current) {
        scrollObserverRef.current.disconnect()
        scrollObserverRef.current = null
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
        scrollTimeoutRef.current = null
      }

      // Disable tracking during auto-scroll to prevent false impressions
      window.isAutoScrolling = true

      const onScrollComplete = () => {
        window.isAutoScrolling = false
        // Clean up observer and timeout
        if (scrollObserverRef.current) {
          scrollObserverRef.current.disconnect()
          scrollObserverRef.current = null
        }
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current)
          scrollTimeoutRef.current = null
        }
        // Track items currently visible in viewport after scroll completes
        if (window.handleImpression) {
          document.querySelectorAll('.js-fast-listing-item[data-attributes]').forEach((card) => {
            const rect = card.getBoundingClientRect()
            const inViewport = rect.top < window.innerHeight && rect.bottom > 0
              && rect.left < window.innerWidth && rect.right > 0
            const visibleEnough = rect.height > 0 && (
              Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0)
            ) / rect.height >= 0.7 // 70% threshold to match useInView

            if (inViewport && visibleEnough) {
              try {
                const attributes = JSON.parse(card.getAttribute('data-attributes'))
                if (attributes && attributes.id) {
                  window.handleImpression(Number(attributes.id))
                }
              } catch (e) {
                // Skip if JSON parsing fails
              }
            }
          })
        }
      }

      // Create observer to detect when scroll target is visible
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            onScrollComplete()
          }
        },
        { threshold: 0.1 }
      )
      scrollObserverRef.current = observer
      observer.observe(pageTopRef.current)

      // Fallback: re-enable tracking after max expected scroll duration
      scrollTimeoutRef.current = setTimeout(() => {
        if (window.isAutoScrolling) {
          onScrollComplete()
        }
      }, 1000)

      pageTopRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [])

  // Cleanup scroll observer and timeout on unmount
  useEffect(() => () => {
    window.isAutoScrolling = false
    if (scrollObserverRef.current) {
      scrollObserverRef.current.disconnect()
    }
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }
  }, [])

  const handleSearchOriginal = useCallback(() => {
    const currentUrl = new URL(window.location.href)
    currentUrl.searchParams.set('disable_fuzzy_matcher', 'true')
    window.location.href = currentUrl.toString()
  }, [])

  useEffect(() => {
    const queryParams = QueryToJson(window.location.search.replace(/^\?/, ''))

    getInitialFilters(queryParams)
  }, [])

  useEffect(() => {
    App.events.subscribe('app:tracking-utils:updated', handleTrackingUpdate)

    return () => {
      App.events.unsubscribe('app:tracking-utils:updated', handleTrackingUpdate)
    }
  }, [])

  function handleTrackingUpdate() {
    setTrackingInfo((prevState) => ({
      ...prevState,
      areUtilsAvailable: true,
    }))
  }

  const fetchTrackingInfo = async (newModels = []) => {
    const modelsForTracking = newModels.length ? newModels : currentModels
    const currentIds = modelsForTracking.map((model) => model.id)
    const haveModelsCurrently = !!currentIds.length
    const localeParam = getLocaleParam()
    const trackingPathSegments = [params.parentCategorySlug, params.subcategorySlug].filter(Boolean)
    let trackingLink = ['/listing_page/tracking_info', ...trackingPathSegments].filter(Boolean).join('/')

    if (!trackingPathSegments.length) {
      const [, ...splitPath] = window.location.pathname.split('/').filter(Boolean)
      trackingLink = ['/listing_page/tracking_info', ...splitPath].filter(Boolean).join('/')
    }

    const queryParts = [
      params.keywords ? `keywords=${params.keywords}` : null,
      params.suggestedKeywords ? `suggested_keywords=${params.suggestedKeywords}` : null,
      haveModelsCurrently ? `item_ids=${currentIds}` : null,
      localeParam ? `locale=${localeParam}` : null,
    ].filter(Boolean)

    const trackingInfoLink = queryParts.length ? `${trackingLink}?${queryParts.join('&')}` : trackingLink

    try {
      const fetchedTrackingInfo = await window.fetchJSON(trackingInfoLink)
      const {
        ga_attributes: gaAttributes,
        trackable_list: trackableList,
        adult_flags: adultFlags,
      } = fetchedTrackingInfo || {}

      if (trackableList) {
        setGaList(fetchedTrackingInfo.trackableList)
      }

      if (gaAttributes) {
        const updatedModels = newModels.map((currentModel) => {
          const { id, attributes } = currentModel
          const modelGaAttributes = gaAttributes.find((model) => model.itemId === Number(id))
          const freshIsAdultContent = adultFlags?.[id] ?? attributes.primaryImage?.isAdultContent
          const updatedModel = {
            ...currentModel,
            attributes: {
              ...attributes,
              primaryImage: {
                ...attributes.primaryImage,
                isAdultContent: freshIsAdultContent,
              },
              gaAttributes: {
                ...modelGaAttributes,
              },
            },
          }

          return updatedModel
        })

        setCurrentModels(updatedModels)

        setTrackingInfo((prevState) => ({
          ...prevState,
          isInfoAvailable: true,
        }))
      }
    } catch (error) {
      // to do: add error handling
      // once Sentry is available in listing page
    }
  }

  function checkIfKeywordIsEnabled(keyword, shouldUseParams) {
    let keywords

    if (shouldUseParams) {
      keywords = params.suggestedKeywords
    } else {
      keywords = selectedFilters.suggested_keywords
    }

    if (keywords && keywords.length) {
      return keywords.split(',').includes(keyword)
    }

    return false
  }

  function parseSuggestions(suggestionsWithImages, shouldUseParams) {
    if (!suggestionsWithImages || !suggestionsWithImages.length) {
      return []
    }

    let result = suggestionsWithImages
      .map((keyword) => {
        const [key, { thumbnailUrl }] = keyword

        return {
          value: key.replaceAll('-', ' '),
          enabled: checkIfKeywordIsEnabled(key, shouldUseParams),
          image: thumbnailUrl,
          onDeselectClick: () => {
            onDeleteTagClick({ value: key })
          },
          onSelectClick: () => {
            const { keywords } = selectedFilters
            const { parentCategorySlug: category } = params

            safeTrackSuggestionClick({
              value: key,
              searchTerm: keywords || category || '',
            })

            onSuggestedKeywordsClick({ suggestedKeyword: key })
          },
        }
      })
      .sort((a, b) => {
        if (a.enabled < b.enabled) {
          return 1
        }
        if (a.enabled > b.enabled) {
          return -1
        }

        return 0
      })

    result = result.filter(
      (keyword) => (keyword.value || '').toLowerCase() !== (selectedFilters.keywords || '').toLowerCase()
    )

    const keywords = selectedFilters.suggested_keywords
    if (keywords && keywords.length) {
      return result.sort((a, b) => {
        const selectedKeywords = keywords.split(',')
        const aIdx = selectedKeywords.indexOf(a.value)
        const bIdx = selectedKeywords.indexOf(b.value)

        if (aIdx !== -1 && bIdx === -1) {
          return -1
        }
        if (bIdx !== -1 && aIdx === -1) {
          return 1
        }
        if (aIdx !== -1 && bIdx !== -1) {
          return aIdx - bIdx
        }
        return 0
      })
    }

    return result
  }

  function formatTags(tagsProp) {
    if (isFirstLoad) {
      setIsFirstLoad(false)
    }

    return parseSuggestions(tagsProp, isFirstLoad)
  }

  const handleFileTypeSort = () => {
    if (!selectedFilters.fileTypes) {
      return
    }

    setCurrentFiltersOptions((prevState) => {
      const currentFilters = prevState.fileTypes?.filters
      const sortedFilters = sortFileTypesBySelection(currentFilters, selectedFilters.fileTypes)

      // sortFileTypesBySelection returns the same reference when the order is unchanged,
      // so we bail out and avoid a redundant context re-render cascade.
      if (!currentFilters || sortedFilters === currentFilters) {
        return prevState
      }

      return { ...prevState, fileTypes: { ...prevState.fileTypes, filters: sortedFilters } }
    })
  }

  useEffect(() => {
    handleFileTypeSort()
  }, [selectedFilters.fileTypes])

  useEffect(() => {
    if (shouldUpdateTags) {
      setTags(formatTags(rawTags))
      setShouldUpdateTags(false)
    }
  }, [rawTags])

  const saveCardSizeSetting = (size) => {
    localStorage.setItem('cardSize', size)
    setCardSizeSetting(size)
    safeTrackingUserSettings({
      currentUserId,
      cardSizeSetting: size,
      cardVisibilitySettings,
    })
  }

  function getCardSizeSetting() {
    const defaultValue = 'medium'

    try {
      const cardSize = localStorage.getItem('cardSize')
      if (['small', 'medium', 'large'].includes(cardSize)) {
        return cardSize
      }

      return defaultValue
    } catch (error) {
      return defaultValue
    }
  }

  const saveCardVisibilitySettings = (settings) => {
    localStorage.setItem('cardVisibility', JSON.stringify(settings))
    setCardVisibilitySettings(settings)
    safeTrackingUserSettings({
      currentUserId,
      cardSizeSetting,
      cardVisibilitySettings: settings,
    })
  }

  function getBooleanValue(value, defaultValue) {
    return typeof value === 'boolean' ? value : defaultValue
  }

  function getCardVisibilitySettings() {
    const defaultValue = {
      title: true,
      type: true,
      price: true,
      format: true,
      quickActionsOnHover: true,
      cgtStandard: !!showCgtVerification,
      suggestions: true,
    }

    try {
      const cardVisibility = localStorage.getItem('cardVisibility')
      const parsedValue = JSON.parse(cardVisibility)

      const mergedCardVisibilitySettings = {
        title: getBooleanValue(parsedValue.title, defaultValue.title),
        type: getBooleanValue(parsedValue.type, defaultValue.type),
        price: getBooleanValue(parsedValue.price, defaultValue.price),
        format: getBooleanValue(parsedValue.format, defaultValue.format),
        quickActionsOnHover: getBooleanValue(parsedValue.quickActionsOnHover, defaultValue.quickActionsOnHover),
        cgtStandard: getBooleanValue(parsedValue.cgtStandard, defaultValue.cgtStandard),
        suggestions: getBooleanValue(parsedValue.suggestions, defaultValue.suggestions),
      }

      return mergedCardVisibilitySettings
    } catch (error) {
      return defaultValue
    }
  }

  function getInitialPageType(value) {
    if (value) {
      return '1'
    }

    return undefined
  }

  function checkCrawlablePages(pages) {
    const compatiblePages = Object.keys(pages).filter((name) => name !== 'is3dPrintModelsPage')

    return compatiblePages.some((value) => !!pages[value])
  }

  const trackFilterChange = useCallback(
    ({ name, value, eventSource = 'filter panel' }) => {
      const { keywords } = selectedFilters
      const { parentCategorySlug: category } = params

      window.filterTracker({
        name,
        value,
        searchTerm: keywords || category || '',
        type: 'model',
        eventSource,
      })

      window.searchTracker({
        searchTerm: keywords || '',
        type: 'model',
      })
    },
    [selectedFilters]
  )

  const followingFilters = (seledtedFilters) => {
    return seledtedFilters?.designers?.following === '1'
  }

  useEffect(() => {
    if (isCurrentUser && followingCount === 0 && followingFilters(selectedFilters)) {
      return setShowNotice(true)
    }

    setShowNotice(false)
  }, [selectedFilters, isCurrentUser, followingCount])

  useEffect(() => {
    dataLayer = window.dataLayer || []
    if (saveSearchSettings !== null && saveSearchSettings !== undefined) {
      const variation_id = saveSearchSettings ? '1' : '0'
      const isShowAllModelsPage =
        window.location.pathname === '/3d-models' || window.location.pathname === '/3d-print-models'

      if (!isShowAllModelsPage) {
        return
      }

      dataLayer.push({
        event: 'experiment_viewed',
        experiment_id: 'save_search_settings',
        variation_id,
      })
    }
  }, [saveSearchSettings])

  useEffect(() => {
    if (hideSuggestionsSection !== null && hideSuggestionsSection !== undefined) {
      dataLayer = window.dataLayer || []

      dataLayer.push({
        event: 'experiment_viewed',
        experiment_id: 'hide_suggestions_section',
        variation_id: hideSuggestionsSection ? '1' : '0',
      })
    }
  }, [hideSuggestionsSection])

  const displayTags = useMemo(() => {
    const qualityVerifiedLabel = i18n.t('browser.filters.cgt_standard_only', {
      ns: 'rails',
      lng: params?.locale || 'en',
    })
    const isQualityVerifiedEnabled = (selectedFilters.licenses || []).includes('cgt_standard_only')
    const i18nOpts = { ns: 'rails', lng: params?.locale || 'en' }
    const tooltipContent = (
      <>
        <strong>{i18n.t('browser.filters.cgt_standard_tooltip_title', i18nOpts)}</strong>{' '}
        {i18n.t('browser.filters.cgt_standard_tooltip_body', i18nOpts)}
      </>
    )
    const qualityVerifiedTag = {
      value: qualityVerifiedLabel,
      enabled: isQualityVerifiedEnabled,
      onDeselectClick: () => {
        const newLicenses = (selectedFilters.licenses || []).filter((l) => l !== 'cgt_standard_only')
        handleFilterChange({ name: 'licenses', value: newLicenses })
        if (typeof window?.removeCurrentSearchFilter === 'function') {
          window.removeCurrentSearchFilter({ name: 'licenses', value: 'cgt_standard_only' })
        }
      },
      onSelectClick: () => {
        const currentLicenses = selectedFilters.licenses || []
        if (!currentLicenses.includes('cgt_standard_only')) {
          trackFilterChange({ name: 'licenses', value: 'cgt_standard_only', eventSource: 'suggestion tags' })
          handleFilterChange({
            name: 'licenses',
            value: [...currentLicenses, 'cgt_standard_only'],
          })
        }
      },
      image: CheckIcon,
      imageAttributes: {
        style: {
          paddingLeft: '10px',
          paddingBlock: '4px',
          border: 'none',
        },
      },
      customProps: {
        tooltip: {
          id: 'cgt-standard-tag',
          content: tooltipContent,
        },
      },
    }
    return [qualityVerifiedTag, ...(tags || [])]
  }, [
    tags,
    selectedFilters.licenses,
    params?.locale,
    handleFilterChange,
    trackFilterChange,
  ])

  // The "Filters" trigger is portaled into the nav's search row (`.js-remaining-filters`) -
  // both the new unified nav and the legacy nav expose that slot. The new nav shows it across
  // the full mobile/tablet range (< md, per its design), so it also needs `sm` (640-959px on
  // desktop) on top of isMobile (real device + xs). The legacy nav must keep its original
  // isMobile-only gate, so widen to `sm` only when the new nav provided the slot (detected via
  // its `unified-nav__filters-slot` class). The slot can also be absent on non-listing search
  // surfaces, so guard against a null container before createPortal.
  const filtersSlotEl = canLoadMobileRemainingFilters ? document.querySelector('.js-remaining-filters') : null
  const isNewNavFiltersSlot = !!filtersSlotEl?.classList.contains('unified-nav__filters-slot')
  // Single source of truth for "the filters live in the nav": drives both the portal target and
  // the `<Filters>` mode, so the desktop filters bar (which renders its own trigger) and the
  // portaled trigger stay mutually exclusive - never both at `sm` on the new nav. Note the model
  // `List` intentionally stays on the narrow `isMobile`; only the filters move at `sm`.
  const isFiltersInNav = isMobile || (isNewNavFiltersSlot && currentBreakpoint === 'sm')
  const remainingFiltersSlot = isFiltersInNav ? filtersSlotEl : null

  // `sortByValue` is an SSR-only prop (`params[:sort_by] || 'best_match'`) that never changes after
  // hydration, so it desyncs from the URL as soon as the user sorts client-side - the sort dropdown
  // and the "All filters" radios keep advertising the server value while the URL and results moved
  // on, and Triangle's `Radio` holds its own checked state, so two options end up highlighted at
  // once. `selectedFilters.sort_by` is the live value; an absent key means the default, which is
  // also what `resetFilters` leaves behind.
  const currentSortByValue = hasHydrated ? (selectedFilters.sort_by || 'best_match') : sortByValue

  return (
    <QueryClientProvider client={queryClient}>
      <Context.Provider
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        value={{
          isLoading,
          sortByOptions,
          filtersOptions: currentFiltersOptions,
          isFiltersSidebarVisible: showFiltersBar,
          resetFilters,
          onSortChange: handleFilterChange,
          sortByValue: currentSortByValue,
          showFilters,
          hideFilters,
          selectedFilters,
          priceFilter: selectedFilters.price,
          polygonFilter: selectedFilters.polygonRange,
          setSelectedFilters: handleFilterChange,
          lastUsedFilters,
          setLastUsedFilters,
          setMultipleFilters: handleFilterChange,
          currentTotalCount: currentMeta.totalCount,
          metaverseModelsCount: currentMeta.metaverseModelsCount,
          allModelsCount: currentMeta.allModelsCount,
          currentMeta,
          viewerIsAdult,
          setViewerIsAdult,
          maxPriceThreshold,
          minPriceThreshold,
          maxPolygonThreshold,
          minPolygonThreshold,
          isCurrentUser,
          saveCardSizeSetting,
          cardSizeSetting,
          saveCardVisibilitySettings,
          cardVisibilitySettings,
          browserAttributes,
          showCgtVerification: true,
          shouldResetPriceRange,
          setShouldResetPriceRange,
          shouldResetPolygonRange,
          setShouldResetPolygonRange,
          currentUserId,
          setSearchEnchantmentAction,
          seoTags,
          isTrackingInfoAvailable: trackingInfo.isInfoAvailable,
          areTrackingUtilsAvailable: trackingInfo.areUtilsAvailable,
          isSimilarSearchPossible,
          tagLinks,
          shouldSwapCrawlingTags: checkCrawlablePages(customPagesChecks),
          trackFilterChange,
          pageCategory: params.parentCategorySlug || params.softwareSlug,
          locale: params.locale,
          subscriptionFlowEnabled,
          premiumListingLabelEnabled,
          subscriptionV17CatalogEnabled,
          downloadAccess,
          openDownloadModal: setDownloadModalHref,
        }}
      >
        <div ref={pageTopRef}>
          {cardVisibilitySettings.suggestions && !hideSuggestionsSection && (
            <div
              className={classnames(
                'listing-header cgt-effect-form-border-enabled',
                displayTags && !displayTags.length && 'listing-header--no-tags'
              )}
            >
              <ModelSearchTags
                tags={displayTags}
                onDeleteTagClick={onDeleteTagClick}
                hideControls
                extractingCoreStyles={extractingCoreStyles}
                useWhiteBackground
              />
            </div>
          )}
          {remainingFiltersSlot && ReactDOM.createPortal(<RemainingFilters />, remainingFiltersSlot)}
        </div>
        <Filters
          isMobile={isFiltersInNav}
          isSuggestionsVisible={cardVisibilitySettings.suggestions && !hideSuggestionsSection}
          extractingCoreStyles={extractingCoreStyles}
          locale={params.locale}
          subscriptionFlowEnabled={subscriptionFlowEnabled}
        />
        <div className="metaverse-listing metaverse-listing--new">
          <div className="metaverse-listing__main listing-page">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <div className="row">
                    {showNotice && <FollowingNotice onClose={() => setShowNotice(false)} />}
                    {currentSuggestedSearch && selectedFilters.keywords && (
                      <div className="col-12">
                        <SuggestedSearchNotice
                          suggestedTerm={currentSuggestedSearch}
                          originalTerm={selectedFilters.keywords}
                          onSearchOriginal={handleSearchOriginal}
                          locale={params.locale}
                        />
                      </div>
                    )}
                    <UpsellBlock cart={cart} />
                    <List
                      extractingCoreStyles={extractingCoreStyles}
                      data={currentModels}
                      cart={cart}
                      shouldTrackSearch={shouldTrackSearch}
                      setShouldTrackSearch={setShouldTrackSearch}
                      setSearchEnchantmentAction={setSearchEnchantmentAction}
                      searchEnchantmentAction={searchEnchantmentAction}
                      gaList={gaList}
                      itemListId={itemListId}
                      currentUserId={currentUserId}
                      isMobile={isMobile}
                      isSalesBoostEnabled={isSalesBoostEnabled}
                      subscriptionBadgesEnabled={subscriptionBadgesEnabled}
                      subscriptionFlowEnabled={subscriptionFlowEnabled}
                      premiumListingLabelEnabled={premiumListingLabelEnabled}
                      isUserLoggedIn={isUserLoggedIn}
                      isSSRModelsData={isSSRModelsData}
                      shouldShowAllItemsOnMobile={shouldShowAllItemsOnMobile}
                    />
                    {downloadModalHref && (
                      <DownloadModal
                        filesHref={downloadModalHref}
                        onClose={() => setDownloadModalHref(null)}
                        locale={params.locale}
                      />
                    )}
                    <Pagination />
                    {!selectedFilters?.pricing?.free && (
                      <FreeItemsSection
                        heading={currentFreeItems.heading}
                        data={currentFreeItems.data}
                        cart={cart}
                        extractingCoreStyles={extractingCoreStyles}
                        isSalesBoostEnabled={isSalesBoostEnabled}
                        subscriptionBadgesEnabled={subscriptionBadgesEnabled}
                      />
                    )}
                    <FooterBanners
                      extractingCoreStyles={extractingCoreStyles}
                      currentUserId={currentUserId}
                      tagLinks={tagLinks}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Context.Provider>
    </QueryClientProvider>
  )
}

ListingPage.propTypes = {
  sortByOptions: array.isRequired,
  filtersOptions: object.isRequired,
  extractingCoreStyles: bool,
  data: array,
  meta: object.isRequired,
  browserAttributes: object,
  itemListId: string,
  tags: array,
  params: object.isRequired,
  seoTags: array,
  isMobileSSR: bool,
  isSimilarSearchPossible: bool,
  saveSearchSettings: bool,
  tagLinks: object,
  isSalesBoostEnabled: bool,
  subscriptionBadgesEnabled: bool,
  hideSuggestionsSection: bool,
  isUserLoggedIn: bool,
  licenseFilter: array,
  suggestedSearch: string,
  shouldShowAllItemsOnMobile: bool,
  freeItems: shape({
    heading: string,
    data: array,
  }),
}

export default ListingPage
